import React, { useState, useEffect, useRef } from 'react';
import { Ship, Package, FileText, Edit2, Trash2, X, Save, AlertTriangle, Plus, Minus, Upload } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { getCloudFrontUrl } from '../../utils/s3Upload';

const SeaExportTable = ({ shipments, onEdit, onDelete, loading = false }) => {
  const [editingShipment, setEditingShipment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [pdfRemoveConfirm, setPdfRemoveConfirm] = useState(null);
  const [isExtractingLeo, setIsExtractingLeo] = useState(false);
  const [editExtractError, setEditExtractError] = useState('');
  const [editContainerNoList, setEditContainerNoList] = useState(['']);
  const [editContainerNoItemErrors, setEditContainerNoItemErrors] = useState([]);
  const [editFormData, setEditFormData] = useState({
    blNumber: '',
    containerNo: [],
    containerQty: '',
    blPdf: null,
    leoPdf: null,
    invPlPdf: null
  });
  
  // Field validation errors for edit form
  const [editFieldErrors, setEditFieldErrors] = useState({
    blNumber: '',
    containerNo: '',
    containerQty: ''
  });

  const openPdfRemoveConfirm = (config) => {
    setPdfRemoveConfirm(config);
  };

  const closePdfRemoveConfirm = () => {
    setPdfRemoveConfirm(null);
  };

  const confirmPdfRemove = () => {
    if (pdfRemoveConfirm?.onConfirm) {
      pdfRemoveConfirm.onConfirm();
    }
    setPdfRemoveConfirm(null);
  };

  const splitPdfValues = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (value instanceof File) return [value];
    return String(value)
      .split(/\s*&\s*|\s*,\s*/)
      .map(v => v.trim())
      .filter(Boolean);
  };

  const getSbCount = (shipment) => {
    const sbRaw = shipment?.sbNo || '';
    const list = String(sbRaw)
      .split(/\s*&\s*|\s*,\s*/)
      .map(v => v.trim())
      .filter(Boolean);
    return Math.max(1, list.length);
  };

  const splitContainerValues = (value) => {
    if (!value) return [];
    const isValidContainerToken = (v) => {
      const t = String(v || '').trim();
      return t !== '' && t !== '-';
    };
    if (Array.isArray(value)) return value.map(v => String(v || '').trim()).filter(isValidContainerToken);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || trimmed === '-' || trimmed === '[]' || trimmed === '[""]') return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map(v => String(v || '').trim()).filter(isValidContainerToken);
        }
      } catch {
        // continue with delimiter parsing
      }
    }
    return String(value)
      .split(/\s*&\s*|\s*,\s*/)
      .map(v => v.trim())
      .filter(isValidContainerToken);
  };

  const formatContainerDisplay = (value) => {
    const normalized = splitContainerValues(value);
    return normalized.length > 0 ? normalized.join(' & ') : '';
  };

  // Validate individual field in edit form
  const validateEditField = (name, value, currentShipment, index = null) => {
    let error = '';
    
    if (name === 'blNumber') {
      const trimmedValue = value?.trim();
      if (trimmedValue) {
        if (trimmedValue.length < 8) {
          error = 'B/L Number must be at least 8 characters long';
        } else if (trimmedValue !== currentShipment?.blNumber) {
          // Only check for duplicates if B/L number has changed
          const duplicate = shipments.find(
            s => s.id !== currentShipment?.id && s.blNumber?.trim().toLowerCase() === trimmedValue.toLowerCase()
          );
          if (duplicate) {
            error = 'This B/L Number already exists in the system';
          }
        }
      }
    } else if (name === 'containerNo') {
      const trimmedValue = value?.trim();
      if (trimmedValue && trimmedValue.length === 11) {
        const containerPattern = /^[A-Za-z]{4}\d{7}$/;
        if (!containerPattern.test(trimmedValue)) {
          error = 'Enter Correct Container No.';
        }
      }

      if (index !== null) {
        setEditContainerNoItemErrors(prev => {
          const qty = Math.max(1, parseInt(editFormData.containerQty, 10) || 1);
          const next = [...(Array.isArray(prev) ? prev : [])];
          while (next.length < qty) next.push('');
          next[index] = error;
          return next;
        });
      }
    } else if (name === 'containerQty') {
      const trimmedValue = value?.trim();
      if (trimmedValue && isNaN(trimmedValue)) {
        error = 'Container Qty must be a number';
      } else if (trimmedValue && parseInt(trimmedValue) <= 0) {
        error = 'Container Qty must be greater than 0';
      }
    }
    
    setEditFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
    
    return error === '';
  };
  
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const topScrollContentRef = useRef(null);
  const tableRef = useRef(null);
  const isScrollingRef = useRef(false);
  
  // Sync width of top scrollbar with table
  useEffect(() => {
    const updateScrollWidth = () => {
      if (tableRef.current && topScrollContentRef.current) {
        const tableWidth = tableRef.current.scrollWidth;
        topScrollContentRef.current.style.width = `${tableWidth}px`;
      }
    };
    
    updateScrollWidth();
    window.addEventListener('resize', updateScrollWidth);
    
    return () => {
      window.removeEventListener('resize', updateScrollWidth);
    };
  }, [shipments]);
  
  // Sync scroll between top and bottom scrollbars
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const bottomScroll = bottomScrollRef.current;
    
    if (!topScroll || !bottomScroll) return;
    
    const handleTopScroll = () => {
      if (!isScrollingRef.current && bottomScroll) {
        isScrollingRef.current = true;
        bottomScroll.scrollLeft = topScroll.scrollLeft;
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      }
    };
    
    const handleBottomScroll = () => {
      if (!isScrollingRef.current && topScroll) {
        isScrollingRef.current = true;
        topScroll.scrollLeft = bottomScroll.scrollLeft;
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      }
    };
    
    topScroll.addEventListener('scroll', handleTopScroll);
    bottomScroll.addEventListener('scroll', handleBottomScroll);
    
    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      bottomScroll.removeEventListener('scroll', handleBottomScroll);
    };
  }, []);

  const handleEditClick = (shipment) => {
    setEditingShipment(shipment);
    setEditExtractError('');
    setEditFieldErrors({
      blNumber: '',
      containerNo: '',
      containerQty: ''
    });
    setEditFormData({
      blNumber: shipment.blNumber || '',
      containerNo: splitContainerValues(shipment.containerNo),
      containerQty: shipment.containerQty || '1',
      blPdf: shipment.blPdf || null,
      leoPdf: splitPdfValues(shipment.leoPdf),
      invPlPdf: shipment.invPlPdf || null
    });
    const qty = Math.max(1, parseInt(shipment.containerQty, 10) || 1);
    const initialContainers = splitContainerValues(shipment.containerNo);
    const padded = Array.from({ length: qty }, (_, idx) => initialContainers[idx] || '');
    setEditContainerNoList(padded.length ? padded : ['']);
    setEditContainerNoItemErrors(Array.from({ length: qty }, () => ''));
  };

  const extractLeoAndSyncData = async (file) => {
    if (!file) return;

    setIsExtractingLeo(true);
    setEditExtractError('');

    try {
      const payload = new FormData();
      payload.append('pdf', file);

      const encodedToken = localStorage.getItem('token');
      let token = null;
      if (encodedToken) {
        try {
          token = atob(atob(encodedToken));
        } catch (error) {
          console.error('Token decode error:', error);
        }
      }

      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE_URL}/scraper/extract-pdf`, {
        method: 'POST',
        headers,
        body: payload
      });

      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Failed to extract LEO PDF data');
      }

      const extractedFields = result?.data || {};
      const modeValue = extractedFields.mode?.trim().toUpperCase();
      if (modeValue && modeValue !== 'LAND' && modeValue !== 'SEA') {
        throw new Error(`Invalid mode in LEO PDF: ${modeValue}. Only LAND/SEA allowed for Sea Export.`);
      }

      const mappedData = {
        portCode: extractedFields.portCode || '',
        sbNo: extractedFields.sbNo || '',
        sbDate: extractedFields.sbDate || '',
        pkg: extractedFields.pkg || '',
        gwt: extractedFields.gwt || '',
        portOfLoading: extractedFields.portOfLoading || '',
        stateOfOrigin: extractedFields.stateOfOrigin || '',
        portOfDischarge: extractedFields.portOfDischarge || '',
        portOfDischargeCode: extractedFields.portOfDischargeCode || '',
        portOfFinalDestinationCode: extractedFields.portOfFinalDestinationCode || '',
        exporterName: extractedFields.exporterName || '',
        exporterAddress: extractedFields.exporterAddress || '',
        countryOfFinalDestination: extractedFields.countryOfFinalDestination || '',
        portOfFinalDestination: extractedFields.portOfFinalDestination || '',
        consigneeName: extractedFields.consigneeName || '',
        buyerName: extractedFields.buyerName || '',
        mode: extractedFields.mode || '',
        lut: extractedFields.lut || '',
        fobValue: extractedFields.fobValue || '',
        dbkClaim: extractedFields.dbkClaim || '',
        igstAmt: extractedFields.igstAmt || '',
        cessAmt: extractedFields.cessAmt || '',
        igstValue: extractedFields.igstValue || '',
        rodtepAmt: extractedFields.rodtepAmt || '',
        rosctlAmt: extractedFields.rosctlAmt || '',
        invNo: extractedFields.invNo || extractedFields.invNoDate || extractedFields.invoiceNoDt || '',
        invAmt: extractedFields.invAmt || '',
        currency: extractedFields.currency || '',
        gstNo: (extractedFields.gstNo || extractedFields.gst_no || '').toUpperCase(),
        leoDate: extractedFields.leoDate || '',
        invoiceNoDt: extractedFields.invoiceNoDt || ''
      };

      setEditingShipment(prev => ({ ...prev, ...mappedData, leoPdf: file }));
    } catch (error) {
      console.error('LEO re-extraction error:', error);
      setEditExtractError(error.message || 'Failed to extract data from uploaded LEO PDF');
    } finally {
      setIsExtractingLeo(false);
    }
  };

  const handleEditInputChange = async (e) => {
    const { name, value, files, type } = e.target;
    if (type === 'file') {
      const selectedFile = files?.[0] || null;
      const selectedFiles = files ? Array.from(files) : [];
      if (name === 'leoPdf') {
        setEditFormData(prev => ({ ...prev, leoPdf: selectedFiles.length > 0 ? selectedFiles : [] }));
      } else {
        setEditFormData(prev => ({ ...prev, [name]: selectedFile }));
      }

      if (name === 'leoPdf' && selectedFiles.length > 0) {
        await extractLeoAndSyncData(selectedFiles[0]);
      }
    } else {
      const finalValue = value;
      setEditFormData(prev => ({ ...prev, [name]: finalValue }));

      if (name === 'containerQty') {
        const safeQty = Math.max(1, parseInt(finalValue, 10) || 1);
        setEditContainerNoList(prev => {
          const next = [...(Array.isArray(prev) ? prev : [])];
          while (next.length < safeQty) next.push('');
          return next.slice(0, safeQty);
        });
        setEditContainerNoItemErrors(prev => {
          const next = [...(Array.isArray(prev) ? prev : [])];
          while (next.length < safeQty) next.push('');
          return next.slice(0, safeQty);
        });
      }
      
      // Trigger real-time validation for specific fields
      if (name === 'blNumber' || name === 'containerQty') {
        validateEditField(name, finalValue, editingShipment);
      }
    }
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    
    // Validate B/L Number: minimum 8 characters
    const blNumberTrimmed = editFormData.blNumber?.trim();
    if (blNumberTrimmed && blNumberTrimmed.length < 8) {
      alert('B/L Number must be at least 8 characters long.');
      return;
    }
    
    // Check for duplicate B/L Number (only if changed)
    if (blNumberTrimmed && blNumberTrimmed !== editingShipment.blNumber) {
      const duplicate = shipments.find(
        s => s.id !== editingShipment.id && s.blNumber?.trim().toLowerCase() === blNumberTrimmed.toLowerCase()
      );
      if (duplicate) {
        alert(`B/L Number "${blNumberTrimmed}" already exists in the system.\n\nPlease use a different B/L Number.`);
        return;
      }
    }
    
    // Validate Container No: must be 11 characters - 4 letters followed by 7 numbers
    const safeQty = Math.max(1, parseInt(editFormData.containerQty, 10) || 1);
    const normalizedContainerNos = Array.from({ length: safeQty }, (_, idx) => String(editContainerNoList[idx] || '').trim().toUpperCase());
    const containerPattern = /^[A-Za-z]{4}\d{7}$/;
    const invalidIndex = normalizedContainerNos.findIndex((cn) => cn && !containerPattern.test(cn));
    if (invalidIndex !== -1) {
      const itemErrors = Array.from({ length: safeQty }, (_, idx) => (
        idx === invalidIndex ? 'Enter Correct Container No.' : ''
      ));
      setEditContainerNoItemErrors(itemErrors);
      setEditFieldErrors(prev => ({
        ...prev,
        containerNo: ''
      }));
      return;
    }

    setEditContainerNoItemErrors(Array.from({ length: safeQty }, () => ''));

    setEditFieldErrors(prev => ({
      ...prev,
      containerNo: ''
    }));

    const sbCount = getSbCount(editingShipment);
    const leoFiles = splitPdfValues(editFormData.leoPdf);
    if (sbCount > 1 && leoFiles.length > 0 && leoFiles.length !== sbCount) {
      alert(`Please upload exactly ${sbCount} LEO PDFs as per SB count.`);
      return;
    }
    
    // Get current user info for editedBy
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const editedBy = currentUser.username || currentUser.name || 'Unknown';
    
    const updatedShipment = {
      ...editingShipment,
      ...editFormData,
      containerNo: normalizedContainerNos,
      editedBy: editedBy,
      updatedAt: new Date().toISOString()
    };
    onEdit(updatedShipment);
    setEditingShipment(null);
  };

  const handleEditCancel = () => {
    setEditingShipment(null);
    setEditFieldErrors({
      blNumber: '',
      containerNo: '',
      containerQty: ''
    });
    setEditFormData({
      blNumber: '',
      containerNo: [],
      containerQty: '',
      blPdf: null,
      leoPdf: null,
      invPlPdf: null
    });
    setEditContainerNoList(['']);
    setEditContainerNoItemErrors([]);
  };

  const handleDeleteClick = (shipment) => {
    setDeleteConfirm(shipment);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      onDelete(deleteConfirm.id);
      setDeleteConfirm(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const readOnlyDetails = [
    ['Port Code', 'portCode'],
    ['SB No', 'sbNo'],
    ['SB Date', 'sbDate'],
    ['Mode', 'mode'],
    ['LUT', 'lut'],
    ['PKG', 'pkg'],
    ['G.WT', 'gwt'],
    ['Port of Loading', 'portOfLoading'],
    ['State of Origin', 'stateOfOrigin'],
    ['Port of Discharge Code', 'portOfDischargeCode'],
    ['Port of Discharge', 'portOfDischarge'],
    ['Country of Final Destination', 'countryOfFinalDestination'],
    ['Port of Final Destination Code', 'portOfFinalDestinationCode'],
    ['Port of Final Destination', 'portOfFinalDestination'],
    ['Exporter Name', 'exporterName'],
    ['Exporter Address', 'exporterAddress'],
    ['GST No', 'gstNo'],
    ['Buyer Name', 'buyerName'],
    ['Consignee Name', 'consigneeName'],
    ['FOB Value', 'fobValue'],
    ['DBK Claim', 'dbkClaim'],
    ['IGST Amt', 'igstAmt'],
    ['Cess Amt', 'cessAmt'],
    ['IGST Value', 'igstValue'],
    ['RODTEP Amt', 'rodtepAmt'],
    ['ROSCTL Amt', 'rosctlAmt'],
    ['INV No', 'invNo'],
    ['INV Amt', 'invAmt'],
    ['Currency', 'currency'],
    ['LEO Date', 'leoDate'],
    ['Invoice No/Dt', 'invoiceNoDt']
  ];

  return (
    <div>
      {/* Top Scrollbar */}
      <div 
        ref={topScrollRef}
        className="overflow-x-auto mb-2"
        style={{ 
          overflowY: 'hidden',
          height: '10px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#9ca3af #e5e7eb',
          touchAction: 'pan-x',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div ref={topScrollContentRef} style={{ height: '1px', width: '3000px' }}></div>
      </div>
      
      {/* Main Table with Bottom Scrollbar */}
      <div 
        ref={bottomScrollRef}
        className="overflow-x-auto bg-white dark:bg-slate-800 rounded-lg shadow"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#9ca3af #e5e7eb',
          touchAction: 'pan-x pan-y',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <table ref={tableRef} className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-16">Sr. No.</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Port Code</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">S/B No</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">S/B Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Mode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">LUT</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">PKG</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">G.WT</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Port Loading</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">State Origin</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Port Discharge Code</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Port Discharge</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Port Final Dest Code</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Port Final Dest</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Country Final Dest</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Exporter Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Buyer Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Consignee Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">FOB Value</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">DBK Claim</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">IGST Amt</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Cess Amt</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">IGST Value</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">RODTEP Amt</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">ROSCTL Amt</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">INV Amt</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Currency</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">LEO Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Invoice No/Dt</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">B/L Number</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Container No</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Container Qty</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">B/L PDF</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">LEO PDF</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">INV/PL PDF</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <tr>
                <td colSpan="36" className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">Loading sea export shipments...</p>
                  </div>
                </td>
              </tr>
            ) : !shipments || shipments.length === 0 ? (
              <tr>
                <td colSpan="36" className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">
                  No sea export shipments found
                </td>
              </tr>
            ) : (
              shipments.map((shipment, index) => (
              <tr
                key={shipment.id || index}
                className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <td className="px-4 py-4 text-center text-sm font-semibold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/30 whitespace-nowrap">
                  {index + 1}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.portCode || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-900 dark:text-white font-medium whitespace-nowrap">
                  {shipment.sbNo || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.sbDate || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    shipment.mode === 'SEA' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    shipment.mode === 'LAND' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                  }`}>
                    {shipment.mode || '-'}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-center text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    shipment.lut === 'Y' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    shipment.lut === 'N' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                    'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                  }`}>
                    {shipment.lut || '-'}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.pkg || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.gwt || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-900 dark:text-white font-medium whitespace-nowrap">
                  {shipment.portOfLoading || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.stateOfOrigin || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.portOfDischargeCode || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-900 dark:text-white font-medium whitespace-nowrap">
                  {shipment.portOfDischarge || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.portOfFinalDestinationCode || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-900 dark:text-white font-medium whitespace-nowrap">
                  {shipment.portOfFinalDestination || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.countryOfFinalDestination || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 max-w-xs truncate">
                  {shipment.exporterName || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 max-w-xs truncate">
                  {shipment.buyerName || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 max-w-xs truncate">
                  {shipment.consigneeName || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-900 dark:text-white font-semibold whitespace-nowrap">
                  {shipment.fobValue ? `₹${shipment.fobValue}` : '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.dbkClaim ? `₹${shipment.dbkClaim}` : '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.igstAmt ? `₹${shipment.igstAmt}` : '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.cessAmt ? `₹${shipment.cessAmt}` : '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.igstValue ? `₹${shipment.igstValue}` : '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.rodtepAmt ? `₹${shipment.rodtepAmt}` : '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.rosctlAmt ? `₹${shipment.rosctlAmt}` : '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.invAmt ? `₹${shipment.invAmt}` : '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.currency || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.leoDate || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.invoiceNoDt || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.blNumber || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {formatContainerDisplay(shipment.containerNo)}
                </td>
                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {shipment.containerQty || '-'}
                </td>
                <td className="px-4 py-4 text-center whitespace-nowrap">
                  {shipment.blPdf ? (
                    <a
                      href={shipment.blPdf instanceof File ? URL.createObjectURL(shipment.blPdf) : getCloudFrontUrl(shipment.blPdf)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition"
                      title="View B/L PDF"
                    >
                      <FileText size={18} />
                      <span className="text-xs font-medium">View</span>
                    </a>
                  ) : (
                    <span className="text-slate-400 dark:text-slate-600 text-xs">-</span>
                  )}
                </td>
                <td className="px-4 py-4 text-center whitespace-nowrap">
                  {splitPdfValues(shipment.leoPdf).length > 0 ? (
                    <a
                      href={splitPdfValues(shipment.leoPdf)[0] instanceof File ? URL.createObjectURL(splitPdfValues(shipment.leoPdf)[0]) : getCloudFrontUrl(splitPdfValues(shipment.leoPdf)[0])}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition"
                      title="View LEO PDF"
                    >
                      <FileText size={18} />
                      <span className="text-xs font-medium">{splitPdfValues(shipment.leoPdf).length > 1 ? `View (${splitPdfValues(shipment.leoPdf).length})` : 'View'}</span>
                    </a>
                  ) : (
                    <span className="text-slate-400 dark:text-slate-600 text-xs">-</span>
                  )}
                </td>
                <td className="px-4 py-4 text-center whitespace-nowrap">
                  {shipment.invPlPdf ? (
                    <a
                      href={shipment.invPlPdf instanceof File ? URL.createObjectURL(shipment.invPlPdf) : getCloudFrontUrl(shipment.invPlPdf)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition"
                      title="View Invoice/Packing List PDF"
                    >
                      <FileText size={18} />
                      <span className="text-xs font-medium">View</span>
                    </a>
                  ) : (
                    <span className="text-slate-400 dark:text-slate-600 text-xs">-</span>
                  )}
                </td>
                <td className="px-4 py-4 text-center whitespace-nowrap">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleEditClick(shipment)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition text-xs font-medium flex items-center gap-1"
                      title="Edit"
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(shipment)}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition text-xs font-medium flex items-center gap-1"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editingShipment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-3 sm:p-4">
          <div
            className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-h-[95vh] overflow-y-auto"
            style={{
              maxWidth: 'min(95vw, 900px)',
              scrollbarWidth: 'thin',
              scrollbarColor: '#2563eb #e5e7eb'
            }}
          >
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-indigo-600 p-6 rounded-t-lg z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Edit2 className="text-white" size={24} />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Edit Sea Export</h2>
                    <p className="text-sm text-blue-100 mt-1">Update shipment details</p>
                  </div>
                </div>
                <button
                  onClick={handleEditCancel}
                  type="button"
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <form onSubmit={handleEditSubmit} className="p-3 sm:p-6 space-y-4 sm:space-y-5">
              {/* Editable Fields (same as create required section) */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 sm:pt-5">
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                  <div className="w-1.5 h-6 bg-gradient-to-b from-purple-600 to-pink-600 rounded"></div>
                  Enter Required Details
                </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5 text-left">B/L Number <span className="text-red-500">*</span></label>
                  <input 
                    type="text" 
                    name="blNumber" 
                    value={editFormData.blNumber} 
                    onChange={handleEditInputChange}
                    className={`w-full px-3 py-2.5 border rounded-lg transition ${
                      editFieldErrors.blNumber
                      ? 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20 text-slate-900 dark:text-white focus:border-red-600 focus:ring-2 focus:ring-red-200'
                      : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200'
                    }`}
                    placeholder="Enter B/L Number"
                  />
                  {editFieldErrors.blNumber && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">
                      ⚠️ {editFieldErrors.blNumber}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5 text-left">Container Qty <span className="text-red-500">*</span></label>
                  <div className={`flex items-center overflow-hidden rounded-xl border ${
                    editFieldErrors.containerQty
                      ? 'border-red-500 dark:border-red-500'
                      : 'border-slate-300 dark:border-slate-600'
                  }`}>
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseInt(editFormData.containerQty, 10) || 1;
                        const next = String(Math.max(1, current - 1));
                        const synthetic = { target: { name: 'containerQty', value: next, type: 'text' } };
                        handleEditInputChange(synthetic);
                      }}
                      className="h-11 w-14 min-w-[56px] shrink-0 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition"
                      aria-label="Decrease container quantity"
                    >
                      <Minus size={16} strokeWidth={3} />
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      name="containerQty"
                      value={editFormData.containerQty}
                      onChange={handleEditInputChange}
                      className={`h-11 flex-1 min-w-0 px-2 text-center text-base font-semibold outline-none ${
                        editFieldErrors.containerQty
                          ? 'bg-red-50 dark:bg-red-900/20 text-slate-900 dark:text-white'
                          : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseInt(editFormData.containerQty, 10) || 1;
                        const next = String(current + 1);
                        const synthetic = { target: { name: 'containerQty', value: next, type: 'text' } };
                        handleEditInputChange(synthetic);
                      }}
                      className="h-11 w-14 min-w-[56px] shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center transition"
                      aria-label="Increase container quantity"
                    >
                      <Plus size={16} strokeWidth={3} />
                    </button>
                  </div>
                  {editFieldErrors.containerQty ? (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">⚠️ {editFieldErrors.containerQty}</p>
                  ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Minimum value: 1</p>
                  )}
                </div>
                <div className={Math.max(1, parseInt(editFormData.containerQty, 10) || 1) > 1 ? 'md:col-span-3' : ''}>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5 text-left">Container No</label>
                  <div className={`grid gap-2 ${Math.max(1, parseInt(editFormData.containerQty, 10) || 1) > 1 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' : 'grid-cols-1'}`}>
                    {Array.from({ length: Math.max(1, parseInt(editFormData.containerQty, 10) || 1) }, (_, idx) => (
                      <div key={idx}>
                        <input
                          type="text"
                          maxLength="11"
                          value={editContainerNoList[idx] || ''}
                          onChange={(e) => {
                            const v = (e.target.value || '').toUpperCase();
                            setEditContainerNoList(prev => {
                              const qty = Math.max(1, parseInt(editFormData.containerQty, 10) || 1);
                              const next = [...(Array.isArray(prev) ? prev : [])];
                              while (next.length < qty) next.push('');
                              next[idx] = v;
                              return next;
                            });
                            validateEditField('containerNo', v, editingShipment, idx);
                          }}
                          placeholder={`Container No ${idx + 1} (ABCU1234567)`}
                          className={`w-full px-3 py-2.5 border rounded-lg transition ${
                            editContainerNoItemErrors[idx]
                            ? 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20 text-slate-900 dark:text-white focus:border-red-600 focus:ring-2 focus:ring-red-200'
                            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200'
                          }`}
                        />
                        {editContainerNoItemErrors[idx] && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">⚠️ {editContainerNoItemErrors[idx]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {editContainerNoItemErrors.every(err => !err) && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Container No fields are auto-generated based on Container Qty.</p>
                  )}
                </div>
              </div>
              </div>

              {/* PDF Uploads */}
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">B/L PDF</label>
                  {editFormData.blPdf ? (
                    <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <a
                          href={
                            editFormData.blPdf instanceof File 
                              ? URL.createObjectURL(editFormData.blPdf) 
                              : getCloudFrontUrl(editFormData.blPdf)
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                        >
                          <FileText size={18} className="flex-shrink-0" />
                          View
                        </a>
                        <button
                          type="button"
                          onClick={() => openPdfRemoveConfirm({
                            title: 'Remove PDF?',
                            message: 'Are you sure you want to remove this B/L PDF?',
                            onConfirm: () => setEditFormData(prev => ({ ...prev, blPdf: null }))
                          })}
                          className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center gap-1"
                        >
                          <X size={18} className="flex-shrink-0" />
                          Clear
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-400">
                      <Upload size={16} className="text-slate-500" />
                      <span className="text-xs text-slate-600">Upload PDF</span>
                      <input type="file" name="blPdf" accept=".pdf" onChange={handleEditInputChange} className="hidden" />
                    </label>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">LEO PDF</label>
                  {splitPdfValues(editFormData.leoPdf).length > 0 ? (
                    <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <a
                          href={splitPdfValues(editFormData.leoPdf)[0] instanceof File ? URL.createObjectURL(splitPdfValues(editFormData.leoPdf)[0]) : getCloudFrontUrl(splitPdfValues(editFormData.leoPdf)[0])}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                        >
                          <FileText size={18} className="flex-shrink-0" />
                          {splitPdfValues(editFormData.leoPdf).length > 1 ? `View (${splitPdfValues(editFormData.leoPdf).length})` : 'View'}
                        </a>
                        <button
                          type="button"
                          onClick={() => openPdfRemoveConfirm({
                            title: 'Remove PDF?',
                            message: 'Are you sure you want to remove this LEO PDF?',
                            onConfirm: () => setEditFormData(prev => ({ ...prev, leoPdf: [] }))
                          })}
                          className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center gap-1"
                        >
                          <X size={18} className="flex-shrink-0" />
                          Clear
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-400">
                      <Upload size={16} className="text-slate-500" />
                      <span className="text-xs text-slate-600">Upload PDF{getSbCount(editingShipment) > 1 ? ` (${getSbCount(editingShipment)})` : ''}</span>
                      <input type="file" name="leoPdf" accept=".pdf" multiple={getSbCount(editingShipment) > 1} onChange={handleEditInputChange} className="hidden" />
                    </label>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">INV/PL PDF</label>
                  {editFormData.invPlPdf ? (
                    <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <a
                          href={
                            editFormData.invPlPdf instanceof File 
                              ? URL.createObjectURL(editFormData.invPlPdf) 
                              : getCloudFrontUrl(editFormData.invPlPdf)
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                        >
                          <FileText size={18} className="flex-shrink-0" />
                          View
                        </a>
                        <button
                          type="button"
                          onClick={() => openPdfRemoveConfirm({
                            title: 'Remove PDF?',
                            message: 'Are you sure you want to remove this INV/PL PDF?',
                            onConfirm: () => setEditFormData(prev => ({ ...prev, invPlPdf: null }))
                          })}
                          className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center gap-1"
                        >
                          <X size={18} className="flex-shrink-0" />
                          Clear
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-400">
                      <Upload size={16} className="text-slate-500" />
                      <span className="text-xs text-slate-600">Upload PDF</span>
                      <input type="file" name="invPlPdf" accept=".pdf" onChange={handleEditInputChange} className="hidden" />
                    </label>
                  )}
                </div>
              </div>

              {(isExtractingLeo || editExtractError) && (
                <div className="space-y-2 -mt-2 mb-4">
                  {isExtractingLeo && (
                    <div className="flex items-center justify-between text-sm text-cyan-600 dark:text-cyan-400 font-medium">
                      <span>Re-extracting data from uploaded LEO PDF...</span>
                      <span>Please wait</span>
                    </div>
                  )}
                  {isExtractingLeo && (
                    <div className="h-2 w-full overflow-hidden rounded-full bg-cyan-100 dark:bg-slate-700">
                      <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 animate-[leoExtractLinearLoad_1.2s_ease-in-out_infinite]"></div>
                    </div>
                  )}
                  {editExtractError && (
                    <p className="text-sm text-red-600 dark:text-red-400 font-medium">{editExtractError}</p>
                  )}
                  <style>{`
                    @keyframes leoExtractLinearLoad {
                      0% { transform: translateX(-120%); }
                      100% { transform: translateX(320%); }
                    }
                  `}</style>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-6 border-t-2 border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={handleEditCancel}
                  className="flex-1 px-6 py-3.5 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3.5 rounded-xl font-semibold shadow-lg flex items-center justify-center gap-2 transition bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                >
                  <Save size={20} />
                  Update
                </button>
              </div>

              {/* Read-only extracted details (same details visible like create modal) */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-5">
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                  <div className="w-1.5 h-6 bg-gradient-to-b from-cyan-600 to-teal-600 rounded"></div>
                  Shipment Details (Read Only)
                </h3>
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                  {readOnlyDetails.map(([label, key]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{label}</label>
                      <input
                        type="text"
                        value={editingShipment?.[key] || '-'}
                        readOnly
                        className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white cursor-not-allowed"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {pdfRemoveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[75] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="text-red-600 dark:text-red-400" size={28} />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center">
                {pdfRemoveConfirm.title || 'Remove PDF?'}
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 text-center">
                {pdfRemoveConfirm.message || 'Are you sure you want to remove this PDF?'}
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={closePdfRemoveConfirm}
                  className="flex-1 px-4 py-2.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmPdfRemove}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium"
                >
                  Yes, Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="text-red-600 dark:text-red-400" size={32} />
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-slate-900 dark:text-white text-center mb-2">
                Delete Sea Export Shipment?
              </h3>
              
              <p className="text-slate-600 dark:text-slate-300 text-center mb-6">
                Are you sure you want to delete this shipment?
                {deleteConfirm.sbNo && (
                  <span className="block mt-2 font-semibold text-slate-900 dark:text-white">
                    S/B No: {deleteConfirm.sbNo}
                  </span>
                )}
                {deleteConfirm.blNumber && (
                  <span className="block text-sm text-slate-600 dark:text-slate-400">
                    B/L Number: {deleteConfirm.blNumber}
                  </span>
                )}
              </p>
              
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-3 mb-6 rounded">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>Warning:</strong> This action cannot be undone.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleDeleteCancel}
                  className="flex-1 px-6 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-white rounded-lg font-medium transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg font-medium shadow-lg flex items-center justify-center gap-2 transition"
                >
                  <Trash2 size={18} />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeaExportTable;
